import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./wallets.css";
import { ArrowLeft, Send } from "lucide-react";
import { toast } from "react-toastify";
import PopupHistory from "../Wallets/History/history";
import WalletReports from "../Wallets/Report/report";
import WalletContracts from "./Contrat/contrat";
import Staking from "./Staking/staking";
import { createPortal } from "react-dom";
const STORAGE_KEY = "mwg_withdraw_state_v3";

const Arrow = () => <span className="menu-arrow" aria-hidden="true"></span>;

const MenuItem = ({
  src,
  alt,
  name,
  sub,
  meta,
  onClick,
  isPrimary,
  iconClass = "img-only",
}) => (
  <div
    className={`menu-item${isPrimary ? " is-primary" : ""}`}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => e.key === "Enter" && onClick?.()}
  >
    <div className={`menu-icon ${iconClass}`}>
      <img src={src} alt={alt} />
    </div>
    <div className="menu-text">
      <div className="menu-name">{name}</div>
      {sub && <div className="menu-sub">{sub}</div>}
    </div>
    {meta && <div className="menu-meta">{meta}</div>}
    <Arrow />
  </div>
);

/* ─────────────────────────────────────────────
   MiniAlert — petite popup compacte pour afficher
   un message (icône + titre + texte + bouton OK).
   Se ferme : bouton OK, clic sur le fond, touche Échap.
   ───────────────────────────────────────────── */
const MiniAlert = ({
  icon,
  title,
  subtitle,
  tone = "info",
  onClose,
  children,
}) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="infoOverlayW is-alert" onClick={onClose}>
      <div
        className={`mini-alert${tone === "warning" ? " is-warning" : ""}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="mini-alert-title"
        onClick={(e) => e.stopPropagation()}
      >
        {icon && (
          <div className="mini-alert-icon">
            <img src={icon} alt="" />
          </div>
        )}
        <h3 className="mini-alert-title" id="mini-alert-title">
          {title}
        </h3>
        {subtitle && <p className="mini-alert-sub">{subtitle}</p>}
        <p className="mini-alert-text">{children}</p>
        <button
          type="button"
          className="mini-alert-btn"
          onClick={onClose}
          autoFocus
        >
          OK
        </button>
      </div>
    </div>
  );
};

const WalletHeroStats = ({
  loading,
  points,
  validPoints,
  invalidPoints,
  withdrawsCount,
  formatPrice,
  variant = "modal",
}) => (
  <div
    className={`wallet-hero-stats-block${variant === "modal" ? " in-modal" : ""}`}
  >
    <div className="wallet-hero">
      <div className="wallet-hero-badge">Active</div>
      <div className="wallet-hero-label">INFO WALLET</div>
    </div>
    <div className="wallet-stats">
      <div className="wallet-stat">
        <div className="wallet-stat-val green">
          {loading ? "—" : validPoints.toLocaleString()}
        </div>
        <div className="wallet-stat-lbl">Valid</div>
      </div>
      <div className="wallet-stat">
        <div className="wallet-stat-val orange">
          {loading ? "—" : invalidPoints.toLocaleString()}
        </div>
        <div className="wallet-stat-lbl">Pending</div>
      </div>
      <div className="wallet-stat">
        <div className="wallet-stat-val">{loading ? "—" : withdrawsCount}</div>
        <div className="wallet-stat-lbl">Withdrawals</div>
      </div>
    </div>
    {/* ── Divider line shown after stats in modal context ── */}
    {variant === "modal" && <div className="modal-stats-divider" />}
  </div>
);

const PopupWallets = ({ onClose }) => {
  const API_URL = process.env.REACT_APP_API_URL;
  const userId = localStorage.getItem("userId");

  const [points, setPoints] = useState(
    parseInt(localStorage.getItem("points")) || 0,
  );
  const [price, setPrice] = useState(
    parseFloat(localStorage.getItem("price")) || 0,
  );
  const [validPoints, setValidPoints] = useState(0);
  const [invalidPoints, setInvalidPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [pointsToWithdraw, setPointsToWithdraw] = useState("");
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showPeriodPopup, setShowPeriodPopup] = useState(false);
  const [showPopupWD, setShowPopupWD] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [blockTimer, setBlockTimer] = useState(0);
  const [verificationAttempts, setVerificationAttempts] = useState(0);

  const [isPopupBalanceFacilityVisible, setIsPopupBalanceFacilityVisible] =
    useState(false);
  const [isPopupBalanceRepaireVisible, setIsPopupBalanceRepaireVisible] =
    useState(false);
  const [isPopupHistoryOpen, setIsPopupHistoryOpen] = useState(false);
  const [isPopupReportOpen, setIsPopupReportOpen] = useState(false);
  const [isPopupContractOpen, setIsPopupContractOpen] = useState(false);

  const [nextValidDate, setNextValidDate] = useState(null);
  const [showValidDatesPopup, setShowValidDatesPopup] = useState(false);
  const [withdraws, setWithdraws] = useState([]);
  const [wallet, setWallet] = useState({
    totalPoints: 0,
    availableAmount: 0,
    pointHistory: [],
  });
  const [isWithdrawBlocked, setIsWithdrawBlocked] = useState(false);
  const [blockedRemainingHours, setBlockedRemainingHours] = useState(0);
  const [blockResendUntil, setBlockResendUntil] = useState(null);

  // ── Staking ──
  const [hasVipPack, setHasVipPack] = useState(false);
  const [showStaking, setShowStaking] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshWallet = () => setRefreshKey((k) => k + 1);

  const popupRef = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const saveVerificationState = (state) => {
    const data = { ...state, savedAt: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
    const params = new URLSearchParams(window.location.search);
    params.set("withdraw", btoa(JSON.stringify(data)).substring(0, 100));
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  const loadVerificationState = () => {
    let data = null;
    try {
      data = JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch (e) {}
    if (!data)
      try {
        data = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      } catch (e) {}
    if (!data) {
      const params = new URLSearchParams(window.location.search);
      const enc = params.get("withdraw");
      if (enc)
        try {
          data = JSON.parse(atob(enc));
        } catch (e) {}
    }
    if (data && Date.now() - data.savedAt < 120000) return data;
    clearVerificationState();
    return null;
  };

  const clearVerificationState = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    const params = new URLSearchParams(window.location.search);
    params.delete("withdraw");
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params}`,
    );
  };

  useEffect(() => {
    const state = loadVerificationState();
    if (state?.withdrawId) {
      const expires = new Date(state.expiresAt).getTime();
      if (Date.now() < expires) {
        setShowVerificationPopup(true);
        setResendTimer(Math.ceil((expires - Date.now()) / 1000));
        setVerificationCode("");
      } else clearVerificationState();
    }
  }, []);

  useEffect(() => {
    if (!showVerificationPopup || resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          toast.error("Code expiré. Veuillez recommencer le retrait.", {
            position: "top-center",
          });
          setShowVerificationPopup(false);
          clearVerificationState();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [showVerificationPopup, resendTimer]);

  useEffect(() => {
    const reopen = () => {
      const state = loadVerificationState();
      if (state && !showVerificationPopup) {
        const expires = new Date(state.expiresAt).getTime();
        if (Date.now() < expires) {
          setShowVerificationPopup(true);
          setResendTimer(Math.ceil((expires - Date.now()) / 1000));
          setVerificationCode("");
        }
      }
    };
    const events = ["focus", "pageshow", "visibilitychange", "popstate"];
    events.forEach((ev) => window.addEventListener(ev, reopen));
    const interval = setInterval(reopen, 3000);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, reopen));
      clearInterval(interval);
    };
  }, [showVerificationPopup]);

  useEffect(() => {
    if (!blockResendUntil) return;
    const interval = setInterval(() => {
      if (Date.now() >= blockResendUntil) setBlockResendUntil(null);
    }, 1000);
    return () => clearInterval(interval);
  }, [blockResendUntil]);

  useEffect(() => {
    const fetchWalletData = async () => {
      setLoading(true);
      try {
        await axios.get(`${API_URL}/update-wallet/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const walletResponse = await axios.get(`${API_URL}/wallets/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const walletData = walletResponse.data;

        try {
          const userResponse = await axios.get(`${API_URL}/user/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          const now = new Date();
          if (
            userResponse.data.withdrawBlockedUntil &&
            now < new Date(userResponse.data.withdrawBlockedUntil)
          ) {
            setIsWithdrawBlocked(true);
            setBlockedRemainingHours(
              Math.ceil(
                (new Date(userResponse.data.withdrawBlockedUntil) - now) /
                  (1000 * 60 * 60),
              ),
            );
          }
        } catch (err) {}

        const now = new Date();
        const invalidPointsCalc =
          walletData.pointHistory?.reduce(
            (sum, entry) =>
              entry?.becomesValidAt && now < new Date(entry.becomesValidAt)
                ? sum + (entry.points || 0)
                : sum,
            0,
          ) || 0;
        const nextValidEntry = walletData.pointHistory?.find(
          (entry) =>
            entry?.becomesValidAt && now < new Date(entry.becomesValidAt),
        );

        setWallet({
          totalPoints: walletData.totalPoints,
          availableAmount: walletData.availableAmount,
          pointHistory: walletData.pointHistory || [],
        });
        setValidPoints(walletData.validPoints);
        setInvalidPoints(invalidPointsCalc);
        setPoints(walletData.totalPoints);
        setPrice(walletData.availableAmount);
        setNextValidDate(
          nextValidEntry ? new Date(nextValidEntry.becomesValidAt) : null,
        );
        localStorage.setItem("points", walletData.totalPoints);
        localStorage.setItem("price", walletData.availableAmount);
        setLastUpdated(new Date(walletData.updatedAt || Date.now()));
      } catch (error) {
        setError(
          error.response?.status === 404
            ? "No wallet found. Please check your purchases or contact support."
            : error.response?.data?.message || "Error fetching wallet data.",
        );
      } finally {
        setLoading(false);
      }
    };

    const fetchWithdrawHistory = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/user/${userId}/withdraws`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setWithdraws(response.data);
      } catch (error) {}
    };

    if (userId) {
      fetchWalletData();
      fetchWithdrawHistory();
    } else {
      setError("User ID not found. Please log in.");
      setLoading(false);
    }
  }, [userId, API_URL, refreshKey]);

  useEffect(() => {
    if (!userId) return;
    const fetchStakingAccess = async () => {
      try {
        const res = await axios.get(`${API_URL}/staking-access/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setHasVipPack(Boolean(res.data.hasVipPack));
      } catch (e) {
        setHasVipPack(false); // en cas d'erreur : Staking caché
      }
    };
    fetchStakingAccess();
  }, [userId, API_URL]);

  const togglePopupWD = () => {
    if (isWithdrawBlocked) {
      toast.error(`Retrait bloqué pour ${blockedRemainingHours} heures.`, {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    validPoints > 0 ? setShowPopupWD(true) : setShowPeriodPopup(true);
  };

  const handleWithdrawChange = (e) => {
    let value = e.target.value.replace(/,/g, ".");
    if (/^\d*\.?\d{0,2}$/.test(value) || value === "")
      setPointsToWithdraw(value);
  };

  const confirmWithdraw = () => {
    const value = parseInt(pointsToWithdraw, 10);
    if (
      !pointsToWithdraw ||
      isNaN(value) ||
      value <= 0 ||
      value > validPoints
    ) {
      setError(`Entrez un nombre valide (1–${validPoints})`);
      return;
    }
    setShowConfirmPopup(true);
  };

  const handleClosePopupWD = () => {
    setShowPopupWD(false);
    setPointsToWithdraw("");
    setError(null);
  };
  const handleMaxClick = () => setPointsToWithdraw(validPoints.toString());

  const handleWithdrawSubmit = async () => {
    const pts = parseInt(pointsToWithdraw, 10);
    if (pts <= 0 || isNaN(pts))
      return toast.error("Points doivent être > 0.", {
        position: "top-center",
      });
    if (validPoints < pts)
      return toast.error(`Seulement ${validPoints} points valides.`, {
        position: "top-center",
      });
    try {
      const response = await axios.post(
        `${API_URL}/user/${userId}/withdraw`,
        { pointsToWithdraw: pts },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      const { withdrawId, expiresIn = 60 } = response.data;
      const expiresAt = new Date(Date.now() + expiresIn * 1000);
      saveVerificationState({ withdrawId, expiresAt: expiresAt.toISOString() });
      setShowVerificationPopup(true);
      setShowConfirmPopup(false);
      setShowPopupWD(false);
      setVerificationCode("");
      setResendTimer(expiresIn);
      toast.success("Code envoyé. Valide 60s.", { position: "top-center" });
      const res = await axios.get(`${API_URL}/user/${userId}/withdraws`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setWithdraws(res.data);
    } catch (error) {
      const msg = error.response?.data?.message || "Erreur retrait.";
      if (error.response?.status === 429) {
        const hours = msg.match(/\d+/)?.[0];
        setIsWithdrawBlocked(true);
        setBlockedRemainingHours(parseInt(hours));
      }
      toast.error(msg, { position: "top-center" });
    }
  };

  const handleVerifyCode = async () => {
    const state = loadVerificationState();
    if (!state || resendTimer === 0) {
      toast.error("Code expiré ou invalide.", { position: "top-center" });
      return;
    }
    try {
      await axios.post(
        `${API_URL}/user/${userId}/withdraw/${state.withdrawId}/verify`,
        { code: verificationCode },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      toast.success("Retrait envoyé à l'admin.", { position: "top-center" });
      setShowVerificationPopup(false);
      clearVerificationState();
      setVerificationCode("");
      setResendTimer(0);
      const res = await axios.get(`${API_URL}/user/${userId}/withdraws`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setWithdraws(res.data);
    } catch (error) {
      const msg = error.response?.data?.message || "Code incorrect.";
      setVerificationAttempts((prev) => prev + 1);
      if (error.response?.status === 429 || verificationAttempts + 1 >= 3) {
        setBlockTimer(24 * 3600);
        toast.error("Trop de tentatives. Bloqué 24h.", {
          position: "top-center",
        });
      }
      toast.error(msg, { position: "top-center" });
    }
  };

  const handleResendCode = async () => {
    if (blockResendUntil && Date.now() < blockResendUntil) {
      toast.error(
        `Attendez ${Math.ceil((blockResendUntil - Date.now()) / 1000)}s avant de renvoyer.`,
        { position: "top-center" },
      );
      return;
    }
    const state = loadVerificationState();
    if (!state) return;
    try {
      const res = await axios.post(
        `${API_URL}/user/${userId}/withdraw/${state.withdrawId}/resend`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );
      const expiresAt = new Date(
        Date.now() + (res.data.expiresIn || 60) * 1000,
      );
      saveVerificationState({ ...state, expiresAt: expiresAt.toISOString() });
      setResendTimer(res.data.expiresIn || 60);
      setVerificationCode("");
      toast.success("Nouveau code envoyé.", { position: "top-center" });
      setBlockResendUntil(Date.now() + 30 * 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Erreur.", {
        position: "top-center",
      });
    }
  };

  const toggleValidDatesPopup = () => setShowValidDatesPopup((p) => !p);
  const togglePopupBalanceFacility = () =>
    setIsPopupBalanceFacilityVisible((p) => !p);
  const togglePopupBalanceRepaire = () =>
    setIsPopupBalanceRepaireVisible((p) => !p);
  const togglePopupHistory = () => setIsPopupHistoryOpen((p) => !p);
  const togglePopupReport = () => setIsPopupReportOpen((p) => !p);
  const togglePopupContract = () => setIsPopupContractOpen((p) => !p);
  const closePopupHistory = () => setIsPopupHistoryOpen(false);
  const closeWalletReports = () => setIsPopupReportOpen(false);
  const closeWalletContracts = () => setIsPopupContractOpen(false);
  const closePeriodPopup = () => setShowPeriodPopup(false);
  const [bdRect, setBdRect] = useState(null);
  const formatPrice = (p) =>
    p === null || p === undefined ? "0.000" : Number(p).toFixed(3);
  const formatDate = (d) =>
    !d
      ? "Date inconnue"
      : new Date(d).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });

  return createPortal(
    <>
      <div
        className="fv-backdrop"
        onClick={onClose}
        style={bdRect ? { inset: "auto", ...bdRect } : undefined}
      />

      <div className="bonus-popup" onClick={onClose}>
        <div
          className="bonus-details-content"
          ref={popupRef}
          onClick={(e) => e.stopPropagation()}
        >
          {isPopupHistoryOpen ? (
            <>
              <div
                className="close_iconHistory"
                role="button"
                tabIndex={0}
                onClick={closePopupHistory}
                onKeyDown={(e) => e.key === "Enter" && closePopupHistory()}
              >
                <ArrowLeft size={13} />
              </div>
              <PopupHistory userId={userId} />
            </>
          ) : isPopupReportOpen ? (
            <>
              <div
                className="close_iconHistory"
                role="button"
                tabIndex={0}
                onClick={closeWalletReports}
                onKeyDown={(e) => e.key === "Enter" && closeWalletReports()}
              >
                <ArrowLeft size={13} />
              </div>
              <WalletReports onBack={closeWalletReports} userId={userId} />
            </>
          ) : isPopupContractOpen ? (
            <>
              <div
                className="close_iconHistory"
                role="button"
                tabIndex={0}
                onClick={closeWalletContracts}
                onKeyDown={(e) => e.key === "Enter" && closeWalletContracts()}
              >
                <ArrowLeft size={13} />
              </div>
              <WalletContracts onBack={closeWalletContracts} userId={userId} />
            </>
          ) : (
            <div className="InfoContainerWallet">
              <WalletHeroStats
                variant="main"
                loading={loading}
                points={points}
                validPoints={validPoints}
                invalidPoints={invalidPoints}
                withdrawsCount={withdraws.length}
                formatPrice={formatPrice}
              />

              <div className="wallet-menu">
                <div className="wallet-section-head">
                  <span className="wallet-section-title">Wallet</span>
                </div>

                <div
                  className="menu-item"
                  role="button"
                  tabIndex={0}
                  onClick={toggleValidDatesPopup}
                  onKeyDown={(e) =>
                    e.key === "Enter" && toggleValidDatesPopup()
                  }
                >
                  <div className="menu-icon img-only">
                    {loading ? (
                      <div className="menu-icon loading" />
                    ) : (
                      <img
                        src="images/packs/wallet/validPoints.jpg"
                        alt="Valid points"
                      />
                    )}
                  </div>
                  <div className="menu-text">
                    <div className="menu-name">
                      {loading
                        ? "Loading…"
                        : `${validPoints.toLocaleString()} pts`}
                    </div>
                    <div className="menu-sub">
                      {loading
                        ? ""
                        : `≈ ${formatPrice(validPoints / 2)} TND available`}
                    </div>
                  </div>
                  <div className="menu-meta">
                    {!loading && <span>Valid</span>}
                  </div>
                  <Arrow />
                </div>

                <MenuItem
                  src="images/packs/wallet/pack_W.png"
                  alt="Withdraw"
                  name="Withdraw"
                  sub="Convert points to TND"
                  isPrimary
                  onClick={togglePopupWD}
                />
                {hasVipPack && (
                  <MenuItem
                    src="images/packs/wallet/pack_S.png"
                    alt="Staking"
                    name="Staking"
                    sub="Earn passive rewards"
                    onClick={() => setShowStaking(true)}
                  />
                )}

                <div className="wallet-section-head">
                  <span className="wallet-section-title">Services</span>
                </div>
                <MenuItem
                  src="images/packs/wallet/BalenceFacilityService.jpg"
                  alt="Balance Facility"
                  name="Balance Facility Service"
                  sub="Flexible credit service"
                  onClick={togglePopupBalanceFacility}
                />
                <MenuItem
                  src="images/packs/wallet/repereservice.png"
                  alt="Balance Repair"
                  name="Balance Repair Service"
                  sub="Fix discrepancies"
                  onClick={togglePopupBalanceRepaire}
                />

                <div className="wallet-section-head">
                  <span className="wallet-section-title">Activity</span>
                  <span
                    className="wallet-section-link"
                    onClick={togglePopupHistory}
                  >
                    See all
                  </span>
                </div>
                <MenuItem
                  src="images/packs/wallet/pack_H.png"
                  alt="History"
                  name="History"
                  sub="All transactions"
                  onClick={togglePopupHistory}
                />
                <MenuItem
                  src="images/facilityService/ReportService.jpg"
                  alt="Report"
                  name="Report"
                  sub="Monthly summaries"
                  onClick={togglePopupReport}
                />
                <MenuItem
                  src="images/facilityService/contract.png"
                  alt="Contracts"
                  name="Contracts"
                  sub="View & manage contracts"
                  onClick={togglePopupContract}
                />
              </div>

              {/* ══════════════════════════════════════════════
                MODAL 1 — WITHDRAW INPUT
            ══════════════════════════════════════════════ */}
              {showPopupWD && (
                <div className="infoOverlayW">
                  <div className="infoModalWallet withdraw-flow-modal">
                    <div className="modal-drag" />

                    <div
                      className="close_icon"
                      role="button"
                      tabIndex={0}
                      onClick={handleClosePopupWD}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleClosePopupWD()
                      }
                    >
                      <ArrowLeft size={13} />
                    </div>

                    <WalletHeroStats
                      variant="modal"
                      loading={loading}
                      points={points}
                      validPoints={validPoints}
                      invalidPoints={invalidPoints}
                      withdrawsCount={withdraws.length}
                      formatPrice={formatPrice}
                    />

                    <div className="modal-body-content">
                      <div className="balance-strip">
                        <span className="bs-label">Available</span>
                        <span className="bs-value">
                          {validPoints.toLocaleString()} pts ·{" "}
                          {formatPrice(validPoints / 2)} TND
                        </span>
                      </div>

                      <div
                        className="balance-type-popup-content"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="withdraw-input-row">
                          <input
                            type="text"
                            className="pointsInput"
                            placeholder="Enter amount…"
                            value={pointsToWithdraw}
                            onChange={handleWithdrawChange}
                          />
                          <div className="input-sep" />
                          <button className="currency" onClick={handleMaxClick}>
                            Max
                          </button>
                          <div className="input-sep" />
                          <button
                            className="currency"
                            onClick={confirmWithdraw}
                          >
                            Send
                          </button>
                        </div>
                        {pointsToWithdraw &&
                          !isNaN(parseFloat(pointsToWithdraw)) && (
                            <div className="input-hint">
                              ≈{" "}
                              <span>
                                {formatPrice(parseFloat(pointsToWithdraw) / 2)}{" "}
                                TND
                              </span>
                            </div>
                          )}
                        {error && <div className="block-message">{error}</div>}
                      </div>
                    </div>

                    <div className="withdraw-btn-row">
                      <button
                        className="btn-cancel-w"
                        onClick={handleClosePopupWD}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-submit-w"
                        onClick={confirmWithdraw}
                      >
                        <Send size={13} />
                        Confirm withdraw
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════════════
                MODAL 2 — CONFIRM
            ══════════════════════════════════════════════ */}
              {showConfirmPopup && (
                <div className="infoOverlayW">
                  <div className="infoModalWallet withdraw-flow-modal">
                    <div className="modal-drag" />

                    <div
                      className="close_icon"
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowConfirmPopup(false)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && setShowConfirmPopup(false)
                      }
                    >
                      <ArrowLeft size={13} />
                    </div>

                    <WalletHeroStats
                      variant="modal"
                      loading={loading}
                      points={points}
                      validPoints={validPoints}
                      invalidPoints={invalidPoints}
                      withdrawsCount={withdraws.length}
                      formatPrice={formatPrice}
                    />

                    <div className="modal-body-content">
                      <div className="confirm-amount-block">
                        <span className="confirm-amount-val">
                          {pointsToWithdraw} pts
                        </span>
                        <div className="confirm-amount-label">
                          ={" "}
                          <strong>
                            {formatPrice(parseFloat(pointsToWithdraw) / 2)} TND
                          </strong>
                        </div>
                      </div>

                      <div className="confirm-details">
                        <div className="confirm-row">
                          <span className="confirm-row-label">
                            Current balance
                          </span>
                          <span className="confirm-row-value gold">
                            {validPoints.toLocaleString()} pts
                          </span>
                        </div>
                        <div className="confirm-row">
                          <span className="confirm-row-label">
                            After withdrawal
                          </span>
                          <span className="confirm-row-value">
                            {(
                              validPoints - parseInt(pointsToWithdraw, 10)
                            ).toLocaleString()}{" "}
                            pts
                          </span>
                        </div>
                        <div className="confirm-row">
                          <span className="confirm-row-label">Processing</span>
                          <span className="confirm-row-value success">
                            Instant
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="confirm-btn-stack">
                      <button
                        className="confirm-btn-yes"
                        onClick={handleWithdrawSubmit}
                      >
                        <Send size={13} />
                        Yes, withdraw
                      </button>
                      <button
                        className="confirm-btn-no"
                        onClick={() => setShowConfirmPopup(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════════════
                MODAL 3 — VERIFICATION CODE
            ══════════════════════════════════════════════ */}
              {showVerificationPopup && (
                <div className="infoOverlayW">
                  <div className="infoModalSend withdraw-flow-modal">
                    <div className="modal-drag" />

                    <button
                      className="close_iconSend"
                      onClick={() => setShowVerificationPopup(false)}
                      aria-label="Close verification"
                    >
                      <ArrowLeft size={13} />
                    </button>

                    <WalletHeroStats
                      variant="modal"
                      loading={loading}
                      points={points}
                      validPoints={validPoints}
                      invalidPoints={invalidPoints}
                      withdrawsCount={withdraws.length}
                      formatPrice={formatPrice}
                    />

                    <div className="modal-body-content">
                      <div className="balance-type-popup-contentSend">
                        <div className="verification-bar">
                          <div
                            className="verification-icon-wrap"
                            onClick={handleVerifyCode}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleVerifyCode()
                            }
                          >
                            <img
                              src="/images/packs/wallet/telegram-icon.png"
                              alt="Verify"
                              className="verification-icon"
                            />
                          </div>
                          <input
                            type="text"
                            className="verification-input1"
                            placeholder="Enter code…"
                            value={verificationCode}
                            onChange={(e) =>
                              setVerificationCode(e.target.value)
                            }
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleVerifyCode()
                            }
                            autoFocus
                            maxLength={8}
                          />
                          <div className="verification-separator" />
                          <div
                            className="timer-text"
                            onClick={
                              resendTimer > 0 ||
                              (blockResendUntil &&
                                Date.now() < blockResendUntil)
                                ? null
                                : handleResendCode
                            }
                            style={{
                              opacity:
                                resendTimer > 0 ||
                                (blockResendUntil &&
                                  Date.now() < blockResendUntil)
                                  ? 0.42
                                  : 1,
                              pointerEvents:
                                resendTimer > 0 ||
                                (blockResendUntil &&
                                  Date.now() < blockResendUntil)
                                  ? "none"
                                  : "auto",
                              cursor: resendTimer > 0 ? "default" : "pointer",
                            }}
                          >
                            {resendTimer > 0
                              ? `00:${resendTimer.toString().padStart(2, "0")}`
                              : "Resend"}
                          </div>
                        </div>

                        <div className="verify-hint">
                          Didn't receive it?{" "}
                          <span
                            onClick={resendTimer > 0 ? null : handleResendCode}
                            style={{
                              opacity: resendTimer > 0 ? 0.42 : 1,
                              pointerEvents: resendTimer > 0 ? "none" : "auto",
                            }}
                          >
                            Resend code
                          </span>
                        </div>
                      </div>
                    </div>

                    <button className="btn-verify" onClick={handleVerifyCode}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#1a0f00"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Verify &amp; submit
                    </button>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════════════
                ALERT 4 — PERIOD (retrait impossible avant 31 jours)
            ══════════════════════════════════════════════ */}
              {showPeriodPopup && (
                <MiniAlert
                  tone="warning"
                  icon="/images/packs/wallet/pack_B.png"
                  title="Not available yet"
                  subtitle="31-day holding period"
                  onClose={closePeriodPopup}
                >
                  Withdrawal is not possible before <strong>31 days</strong>{" "}
                  from purchase.
                  {nextValidDate && (
                    <>
                      {" "}
                      Next valid date:{" "}
                      <strong>{formatDate(nextValidDate)}</strong>
                    </>
                  )}
                </MiniAlert>
              )}

              {/* ══════════════════════════════════════════════
                MODAL 5 — STAKING (VIP uniquement)
            ══════════════════════════════════════════════ */}
              {showStaking && hasVipPack && (
                <Staking
                  validPoints={validPoints}
                  onClose={() => setShowStaking(false)}
                  onChanged={refreshWallet}
                />
              )}

              {/* ══════════════════════════════════════════════
                ALERT 6 — BALANCE FACILITY SERVICE
            ══════════════════════════════════════════════ */}
              {isPopupBalanceFacilityVisible && (
                <MiniAlert
                  icon="/images/packs/wallet/BalenceFacilityService.jpg"
                  title="Balance Facility Service"
                  subtitle="Flexible credit service"
                  onClose={togglePopupBalanceFacility}
                >
                  The Balance Facility Service allows you to access funds ahead
                  of your valid date. Contact support to activate.
                </MiniAlert>
              )}

              {/* ══════════════════════════════════════════════
                ALERT 7 — BALANCE REPAIR SERVICE
            ══════════════════════════════════════════════ */}
              {isPopupBalanceRepaireVisible && (
                <MiniAlert
                  icon="/images/packs/wallet/repereservice.png"
                  title="Balance Repair Service"
                  subtitle="Fix discrepancies"
                  onClose={togglePopupBalanceRepaire}
                >
                  If your balance is incorrect, the Balance Repair Service will
                  investigate and correct any discrepancies. Please allow up to
                  48 hours.
                </MiniAlert>
              )}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default PopupWallets;